import {
  Autocomplete,
  Box,
  CircularProgress,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ExternalDriverResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { ColoredLabel, HighlightedText, PlusIcon, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { DriverAutoCompleteStore } from './DriverAutoCompleteStore'

interface Props {
  initialValueName: string
  label: string
  selectedDriver?: ExternalDriverResponseDto | null
  disabled?: boolean
  required?: boolean
  helperText?: string
  error?: boolean
  searchBy?: 'name' | 'driverID'
  dataCy?: string
  onChange: (driver: ExternalDriverResponseDto | undefined | null) => void
}
export const DriverAutoComplete = observer(
  ({
    initialValueName,
    label,
    selectedDriver,
    disabled,
    required,
    helperText,
    error,
    searchBy = 'name',
    onChange,
    dataCy,
  }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { externalDriverViewStore } = usePlanningStore()

    const store = useMemo(() => new DriverAutoCompleteStore(), [])

    useEffect(() => {
      store.setFilteringByName(searchBy === 'name')
    }, [store, searchBy])

    const [inputValue, setInputValue] = useState<string>('')

    const addDriver = useCallback(
      (e?: MouseEvent | KeyboardEvent) => {
        e?.preventDefault()
        e?.stopPropagation()

        externalDriverViewStore.toggleAddDialog(
          true,
          {
            [initialValueName]: inputValue,
          },
          externalDriver => {
            onChange(externalDriver)
          },
        )
      },
      [externalDriverViewStore, inputValue, onChange],
    )

    const noOptionsText = useMemo(() => {
      return !inputValue || inputValue.trim() === '' || store.isLoading
        ? t('startTypingToSearch', 'Start typing to search...')
        : ''
    }, [inputValue, store.isLoading, t])

    const renderOptionItems = useCallback(
      (props: any, option: ExternalDriverResponseDto) => {
        const addNewDriverOption = (
          <Box
            onClick={e => addDriver(e)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              paddingTop: 0.6,
              paddingBottom: 0.6,
            }}
          >
            <ListItemText>{t('addNew', 'Add new')}</ListItemText>
            <PlusIcon />
          </Box>
        )

        const driverOptionItem = (
          <>
            <Stack>
              <Typography variant='body1'>
                <HighlightedText highlight={inputValue} color={theme.palette.info.main}>
                  {searchBy === 'name' ? option.name : option.driverID}
                </HighlightedText>
              </Typography>
              <Typography variant='caption' color='secondary'>
                {searchBy === 'name' ? option.driverID : option.name}
              </Typography>
            </Stack>
            {option.banned && <ColoredLabel color='warning' label={t('banned', 'Banned')} />}
          </>
        )

        const optionItem = (
          <Box component='li' {...props} key={`location_${option.id}`}>
            <Stack
              flexDirection='row'
              justifyContent='space-between'
              alignItems='center'
              gap={1}
              width='100%'
            >
              {option.id === 0 ? addNewDriverOption : driverOptionItem}
            </Stack>
          </Box>
        )

        return optionItem
      },
      [addDriver, inputValue, searchBy, t, theme.palette.info.main],
    )

    return (
      <Autocomplete
        disablePortal
        forcePopupIcon={false}
        disabled={disabled}
        getOptionLabel={(driver: ExternalDriverResponseDto) =>
          (searchBy === 'name' ? driver.name : driver.driverID) ?? t('unknown', 'Unknown')
        }
        options={store.options}
        filterOptions={x => x}
        defaultValue={selectedDriver ?? null}
        value={selectedDriver ?? null}
        isOptionEqualToValue={(
          option: ExternalDriverResponseDto,
          value: ExternalDriverResponseDto,
        ) => option.id === value.id}
        onChange={(_, selectedDriver: ExternalDriverResponseDto | null) => {
          if (selectedDriver?.id === 0) {
            addDriver()
            return
          }
          onChange(selectedDriver)
        }}
        getOptionDisabled={option => option.banned}
        renderOption={renderOptionItems}
        noOptionsText={noOptionsText}
        renderInput={params => (
          <TextField
            {...params}
            required={required}
            label={<>{label ?? t('truckDriver', 'Truck driver')} </>}
            data-cy={dataCy ?? 'external-driver-autocomplete'}
            helperText={helperText}
            error={error}
            onBlur={() => {
              if (!selectedDriver) {
                setInputValue('')
                store.setOptions([])
              }
            }}
            onChange={e => {
              if (!e.target.value) return

              store.setLoading(true)
              store.filterOptions(e.target.value)
              setInputValue(e.target.value)
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {store.isLoading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !selectedDriver && store.options.length === 1) {
                e.preventDefault()
                addDriver(e)
              }
            }}
          />
        )}
      />
    )
  },
)
